import FileDownloader from "@/components/FileDownloader/FileDownloader";
import NoImpactBanner from "@/components/NoImpactBanner/NoImpactBanner";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import { useCompanyAccess } from "@/hooks/useCompanySettings";
import { ExportOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button } from "antd";
import axios from "axios";
import { Link } from "react-router-dom";
import MetricWidget from "../components/MetricWidget/MetricWidget";
import YearPicker from "../components/YearPicker/YearPicker";
import { useDashboardYear } from "../hooks/useDateRange";
import { useDashboardOrderCounts } from "../hooks/usePoSo";
import { formatCarbonFootprint } from "../utils/formatters";
import styles from "./GhgDashboard.module.scss";
import GhgTable from "./GhgTable";
import GhgTreemap from "./GhgTreemap";
import { IGetGhg } from "./types";
import PremiumExplore from "@/components/PremiumFeatureComponents/PremiumExplore";
import { IMPACT_METRICS } from "@/pages/dashboard/utils/types";

export default function GhgDashboard() {
  const { year } = useDashboardYear();
  const access = useCompanyAccess();

  const pageTitle = `GHG report`;
  const pageDescription =
    "This GHG report outlines your company's emissions across Scopes 1, 2, and 3, breaking them down by categories such as combustion sources, purchased utilities, and value chain impacts from upstream/downstream product impacts and travel.";

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", "ghg", year],
    enabled: !!year,
    queryFn: () =>
      axios
        .get<IGetGhg>("/ghg", {
          params: {
            year,
          },
        })
        .then((result) => result.data),
  });

  const { data: orderCounts } = useDashboardOrderCounts({});

  const exportGhg = async (): Promise<string> => {
    const { data } = await axios.get<{ url: string }>(`/ghg/export`, {
      params: {
        year,
      },
    });
    return data.url;
  };

  if (access && !access.includes("ghg"))
    return (
      <PremiumExplore
        title={pageTitle}
        description={pageDescription}
      />
    );

  return (
    <div className={styles.grid}>
      <div>
        <PageHeader
          title={pageTitle}
          description={pageDescription}
          right={
            <>
              <YearPicker />
              <FileDownloader
                fileOutputName={`export-ghg.xlsx`}
                fileUrlFn={exportGhg}
                buttonText={<ExportOutlined />}
                tooltipText="Export"
                addTimestampAndCompany={true}
              />
            </>
          }
        />
      </div>
      {orderCounts?.total === 0 ? (
        <NoImpactBanner title={`No impact for ${year}`}>
          <span>No purchase and sales orders have been uploaded for {year} so far.</span>
          <span>
            Start with uploading orders and continue adding data on materials, usage, end of life,
            facilities and transport.
          </span>
          <Link to="/orders/purchase_orders">
            <Button type="primary">Upload orders</Button>
          </Link>
        </NoImpactBanner>
      ) : data?.total === 0 ? (
        <NoImpactBanner title={`No impact for ${year}`}>
          <span>What to do?</span>
          <ul>
            <li>
              Upload bill of materials for <Link to="/products">products</Link>.
            </li>
            <li>
              Match the <Link to="/materials">materials</Link> with their impact references.
            </li>
            <li>
              Add utility sources for <Link to="/facilities">facilities</Link>.
            </li>
            <li>
              Upload <Link to="/transport/inbound">transport</Link> data.
            </li>
            <li>
              Provide <Link to="/usage">usage</Link> and <Link to="/eol">end-of-life</Link> data per
              product.
            </li>
          </ul>
        </NoImpactBanner>
      ) : (
        <>
          <MetricWidget
            title={"Total carbon impact"}
            data={{ value: data?.total }}
            format={formatCarbonFootprint}
            metric={IMPACT_METRICS.carbon_footprint}
            loading={isLoading}
          />
          <div className={styles.columns}>
            <GhgTable
              data={data}
              loading={isLoading}
            />
            <GhgTreemap
              data={data}
              loading={isLoading}
            />
          </div>
        </>
      )}
    </div>
  );
}
